import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { RefreshCw, Search, Star, StarOff } from "lucide-react";
import { toast } from "sonner";
import { api, type Forum } from "@/lib/api";
import { useToggleFollow } from "@/hooks/useToggleFollow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CategoryBadge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { PriceTag, VariationBadge } from "@/components/PriceTag";
import { IntroBanner } from "@/components/IntroBanner";
import { TrendBadge } from "@/components/TrendBadge";
import { ForumCard } from "@/components/cards/ForumCard";
import { NativeSelect } from "@/components/ui/native-select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { SortableHeader } from "@/components/ui/sortable-header";
import { useSort } from "@/components/ui/sortable";
import { usePathTab } from "@/lib/usePathTab";
import { useSeo } from "@/lib/useSeo";
import { useRequireAuth, useAuth } from "@/hooks/useAuth";
import { fromNowFR, formatNumber, cn } from "@/lib/utils";

const FORUM_CATS = ["all", "followed", "hot", "active", "quiet", "dormant", "dead"] as const;
type ForumCat = (typeof FORUM_CATS)[number];

const CAT_LABELS: Record<ForumCat, string> = {
  all: "Tous",
  followed: "Suivis",
  hot: "Chauds",
  active: "Actifs",
  quiet: "Calmes",
  dormant: "Dormants",
  dead: "Morts",
};

type ForumSortKey =
  | "title"
  | "category"
  | "last_price"
  | "last_variation_pct"
  | "active_score"
  | "threads_window"
  | "posts_window"
  | "last_reply_at";

// Activity window selected for the "Nouveaux sujets" + "Activité" columns.
// A single picker placed above the table controls both. Persisted across
// reloads via localStorage.
type ActivityWindow = "24h" | "7d";

export default function ForumsPage() {
  useSeo({
    title: "Forums Boursorama en temps réel",
    description:
      "Classement en direct des forums Boursorama les plus actifs : valeurs discutées, sentiment, tendance d'activité et cours associés.",
    canonicalPath: "/forums",
  });
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [cat, setCat] = usePathTab<ForumCat>("cat", "/forums", "all", FORUM_CATS);
  // Wraps any auth-gated action with a French toast + CTA toward /login
  // when the user is anonymous. The wrapped mutation only runs once we
  // know the user is signed in.
  const gate = useRequireAuth();
  // The "refresh prices" endpoint is auth-gated server-side (RequireUser) ;
  // hide the button for anonymous visitors so they don't hit a 401.
  const { isAuthenticated } = useAuth();

  // Forums list. When the user picks a specific activity bucket we forward
  // the filter to the API so the response contains the full bucket, not just
  // the top-500 by score. The "followed" tab keeps the default query because
  // we filter it client-side against the follows cache.
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["forums", cat === "followed" ? "all" : cat],
    queryFn: () => api.forums({ category: cat === "followed" ? undefined : cat }),
  });
  const { data: follows = [] } = useQuery({
    queryKey: ["follows"],
    queryFn: () => api.follows(),
    retry: false,
  });
  const { data: globalCats = [] } = useQuery({
    queryKey: ["stats-categories"],
    queryFn: () => api.statsCategories(),
    staleTime: 60_000,
  });
  // Global counts come from the unbounded /stats/categories endpoint,
  // so they reflect the whole DB, not just the 500 we display.
  const globalCounts = useMemo(() => {
    const c: Record<string, number> = {};
    let total = 0;
    for (const row of globalCats) {
      c[row.category] = row.count;
      total += row.count;
    }
    c.all = total;
    return c;
  }, [globalCats]);
  const followedIds = useMemo(
    () => new Set(follows.filter((f: any) => f.target_type === "forum").map((f: any) => Number(f.target_id))),
    [follows]
  );

  // "Refresh prices" : enqueue a fast price-only scrape of the top-N forums.
  // The user can click again or wait — the table polls /forums every minute.
  const refreshPrices = useMutation({
    mutationFn: (limit: number) => api.refreshTopPrices(limit),
    onSuccess: (r) => {
      toast.success(`${r.queued} forum(s) en file de rafraîchissement — résultats dans quelques secondes`);
      // Auto-refresh the forums list a few times to pick up new prices.
      setTimeout(() => qc.invalidateQueries({ queryKey: ["forums"] }), 4000);
      setTimeout(() => qc.invalidateQueries({ queryKey: ["forums"] }), 10000);
      setTimeout(() => qc.invalidateQueries({ queryKey: ["forums"] }), 20000);
    },
    onError: (e) => toast.error(`Erreur : ${e instanceof Error ? e.message : "?"}`),
  });

  // Shared optimistic follow/unfollow (instant star, rollback on error).
  const toggleFollow = useToggleFollow("forum");
  const toggle = {
    mutate: (forum: Forum) =>
      toggleFollow.mutate({ id: forum.id, label: forum.title || forum.slug }),
    isPending: toggleFollow.isPending,
  };

  const filtered = useMemo(() => {
    const list = (data ?? []) as Forum[];
    const ql = q.toLowerCase();
    return list.filter((f) => {
      if (ql && !(`${f.title} ${f.slug}`.toLowerCase().includes(ql))) return false;
      // "followed" is the only filter we still apply client-side ; activity
      // category is already enforced server-side via the API ?category=.
      if (cat === "followed") return followedIds.has(f.id);
      return true;
    });
  }, [data, q, cat, followedIds]);

  // Sortable columns. Default = active_score DESC (API ordering).
  const { state: sortState, toggle: sortToggle, sort } = useSort<ForumSortKey, Forum>(
    "active_score",
    "desc"
  );

  // Window applied to the "Sujets" and "Messages" columns. We persist it
  // across remounts so the user keeps the same view when switching tabs.
  const [activityWindow, setActivityWindow] = useState<ActivityWindow>(() => {
    try {
      const v = localStorage.getItem("boursicote.forums.window");
      return v === "7d" ? "7d" : "24h";
    } catch {
      return "24h";
    }
  });
  function flipWindow() {
    const next: ActivityWindow = activityWindow === "24h" ? "7d" : "24h";
    setActivityWindow(next);
    try {
      localStorage.setItem("boursicote.forums.window", next);
    } catch {
      /* noop */
    }
  }

  // Helpers — pull the right (cur, prev) pair for the active window.
  const threadsCur = (f: Forum) => (activityWindow === "24h" ? f.threads_24h : f.threads_7d);
  const threadsPrev = (f: Forum) =>
    activityWindow === "24h" ? f.threads_24h_prev : f.threads_7d_prev;
  const postsCur = (f: Forum) => (activityWindow === "24h" ? f.posts_24h : f.posts_7d);
  const postsPrev = (f: Forum) =>
    activityWindow === "24h" ? f.posts_24h_prev : f.posts_7d_prev;

  // Trend %, with the same sentinel rules used by <TrendBadge> :
  //   prev == 0 && cur >  0  →  +Infinity  (sort to the top as "new")
  //   prev == 0 && cur == 0  →  NaN        (sort to the bottom)
  //   prev >  0              →  signed %
  function trendPct(cur: number, prev: number): number {
    if (prev === 0) return cur > 0 ? Number.POSITIVE_INFINITY : Number.NaN;
    return ((cur - prev) / prev) * 100;
  }

  const sortedFiltered = useMemo(
    () =>
      sort(filtered, {
        title: (f) => f.title || f.slug,
        category: (f) => f.category,
        last_price: (f) => f.last_price,
        last_variation_pct: (f) => f.last_variation_pct,
        active_score: (f) => f.active_score,
        // The "Nouveaux sujets" / "Activité" columns sort by count primarily ;
        // the trend % is a secondary signal shown in-cell as a TrendBadge.
        threads_window: (f) => threadsCur(f),
        posts_window: (f) => postsCur(f),
        last_reply_at: (f) => f.last_reply_at,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, sort, activityWindow]
  );

  // Use global counts (whole DB) for the filter pill numbers, not the
  // truncated 500-row list — otherwise filtering looks like there are
  // "0 dead" when in fact we just didn't load them.
  const counts = globalCounts;

  return (
    <div className="flex flex-col gap-6">
      {/* One-time explainer banner. Dismissed state lives in localStorage,
          so users only see it on their first visit per device. */}
      <IntroBanner />

      <header className="flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-3 min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold shrink-0">Forums</h1>
          {/* The count line is essentially metadata — only show it on sm+ to
              keep the mobile header tight and readable. */}
          <p className="hidden sm:block text-sm text-foreground-muted truncate">
            {formatNumber(globalCounts.all ?? 0)} indexés ·{" "}
            {formatNumber(data?.length ?? 0)} affichés ·{" "}
            {formatNumber(follows.filter((f: any) => f.target_type === "forum").length)}{" "}
            suivi(s)
          </p>
        </div>
        {isAuthenticated && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => refreshPrices.mutate(50)}
            disabled={refreshPrices.isPending}
            title="Re-scrape les 50 forums les plus actifs pour mettre à jour les cotations"
            aria-label="Rafraîchir les prix du top 50"
            className="shrink-0"
          >
            <RefreshCw className={cn("size-3.5", refreshPrices.isPending && "animate-spin")} />
            <span className="hidden md:inline">Rafraîchir les prix</span>
          </Button>
        )}
      </header>

      <Card>
        <CardContent className="flex flex-col gap-3 p-4">
          {/* ─ Search bar (always visible, full width) ─ */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-foreground-muted pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un forum…"
              className="pl-8"
              type="search"
            />
          </div>

          {/* ─ Filters row : native select on mobile, pill group on sm+ ─
              The native select pops the OS picker on touch devices, which
              is far more usable than 7 wrapping pills on a 360 px screen. */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:gap-4 gap-3">
            <NativeSelect
              className="sm:hidden"
              label="Catégorie"
              value={cat}
              onValueChange={(v) => setCat(v as ForumCat)}
              options={FORUM_CATS.map((c) => ({
                value: c,
                label: CAT_LABELS[c],
                count: c === "followed" ? followedIds.size : counts[c] ?? 0,
              }))}
            />
            <div className="hidden sm:flex flex-1 flex-col gap-1">
              <span className="text-xs font-medium text-foreground-muted">Catégorie</span>
              <div className="flex flex-wrap gap-2">
                {FORUM_CATS.map((c) => {
                  const n =
                    c === "followed" ? followedIds.size : counts[c] ?? 0;
                  return (
                    <Button
                      key={c}
                      variant={cat === c ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCat(c)}
                      title={c === "followed" ? "Forums que vous suivez" : undefined}
                    >
                      {CAT_LABELS[c]}
                      <span className="ml-1 text-xs opacity-70">({n})</span>
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* ─ Period picker : compact tab strip, always visible ─ */}
            <div className="flex flex-col gap-1 sm:shrink-0">
              <span className="text-xs font-medium text-foreground-muted">Période</span>
              <div
                role="tablist"
                aria-label="Période d'activité"
                className="inline-flex items-center gap-1 rounded-md border border-border bg-surface p-1 self-start"
              >
                {(["24h", "7d"] as const).map((w) => {
                  const selected = activityWindow === w;
                  return (
                    <button
                      key={w}
                      role="tab"
                      aria-selected={selected}
                      onClick={() => {
                        if (!selected) flipWindow();
                      }}
                      className={cn(
                        "h-8 rounded-sm px-3 text-xs font-medium transition-colors",
                        selected
                          ? "bg-primary text-primary-foreground"
                          : "text-foreground-muted hover:bg-surface-muted hover:text-foreground"
                      )}
                    >
                      {w === "24h" ? "24 h" : "7 j"}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="py-10 text-center text-sm flex flex-col items-center gap-3">
            <span className="text-foreground-muted">
              Impossible de charger les forums (erreur réseau).
            </span>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Réessayer
            </Button>
          </CardContent>
        </Card>
      ) : sortedFiltered.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-foreground-muted text-sm">
            Aucun forum ne correspond à ces filtres.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ── Mobile : vertical list of ForumCards ── */}
          <div className="md:hidden flex flex-col gap-2">
            {sortedFiltered.slice(0, 200).map((f, i) => (
              <ForumCard
                key={f.id}
                forum={f}
                rank={i + 1}
                isFollowed={followedIds.has(f.id)}
                onToggleFollow={() =>
                  gate(() => toggle.mutate(f), { actionLabel: "suivre un forum" })
                }
                windowLabel={activityWindow}
                threadsCur={threadsCur(f)}
                threadsPrev={threadsPrev(f)}
                postsCur={postsCur(f)}
                postsPrev={postsPrev(f)}
              />
            ))}
          </div>

          {/* ── Desktop : full table ── */}
          <Card className="hidden md:block">
            <Table>
            <TableHeader>
              <TableRow>
                {/* # column : hidden on mobile to save room */}
                <TableHead className="w-10 hidden sm:table-cell">#</TableHead>

                {/* Forum title : sticky-left so it stays in view when the
                    user scrolls the table horizontally on mobile. */}
                <SortableHeader
                  active={sortState.key === "title"}
                  dir={sortState.dir}
                  onSort={() => sortToggle("title")}
                  className="sticky left-0 bg-surface z-10 min-w-[12rem]"
                >
                  Forum
                </SortableHeader>

                {/* Category badge — hidden < md */}
                <SortableHeader
                  active={sortState.key === "category"}
                  dir={sortState.dir}
                  onSort={() => sortToggle("category")}
                  className="hidden md:table-cell"
                >
                  Catégorie
                </SortableHeader>

                {/* Cours — desktop only (lg+) */}
                <SortableHeader
                  className="text-right hidden lg:table-cell"
                  active={sortState.key === "last_price"}
                  dir={sortState.dir}
                  onSort={() => sortToggle("last_price")}
                  title="Cours de bourse actuel (donnée Boursorama)"
                >
                  Cours
                </SortableHeader>

                {/* Var. cours — md+ */}
                <TableHead className="text-right hidden md:table-cell">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <SortableHeader
                          active={sortState.key === "last_variation_pct"}
                          dir={sortState.dir}
                          onSort={() => sortToggle("last_variation_pct")}
                        >
                          Var. cours
                        </SortableHeader>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Variation du cours en pourcentage depuis la veille
                      (donnée Boursorama). Différent de l'évolution d'activité
                      du forum, à droite.
                    </TooltipContent>
                  </Tooltip>
                </TableHead>

                {/* Score — lg+ */}
                <TableHead className="text-right hidden lg:table-cell">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <SortableHeader
                          active={sortState.key === "active_score"}
                          dir={sortState.dir}
                          onSort={() => sortToggle("active_score")}
                        >
                          Score
                        </SortableHeader>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Score d'activité Boursicote, de 0 à 100, basé sur le
                      volume et la fraîcheur des messages sur 30 jours.
                      Forums à fort score = conversations actives en ce moment.
                    </TooltipContent>
                  </Tooltip>
                </TableHead>

                {/* Nouveaux sujets — always visible (primary metric) */}
                <TableHead className="text-right">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <SortableHeader
                          active={sortState.key === "threads_window"}
                          dir={sortState.dir}
                          onSort={() => sortToggle("threads_window")}
                        >
                          Sujets
                        </SortableHeader>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Nombre de sujets créés sur la période sélectionnée
                      ({activityWindow === "24h" ? "24 dernières heures" : "7 derniers jours"}),
                      avec l'évolution % par rapport à la période précédente.
                    </TooltipContent>
                  </Tooltip>
                </TableHead>

                {/* Activité — always visible (primary metric) */}
                <TableHead className="text-right">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <SortableHeader
                          active={sortState.key === "posts_window"}
                          dir={sortState.dir}
                          onSort={() => sortToggle("posts_window")}
                        >
                          Activité
                        </SortableHeader>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Nombre total de messages publiés sur la période
                      ({activityWindow === "24h" ? "24 dernières heures" : "7 derniers jours"}),
                      avec l'évolution % par rapport à la période précédente.
                    </TooltipContent>
                  </Tooltip>
                </TableHead>

                {/* Dernier post — lg+ */}
                <SortableHeader
                  active={sortState.key === "last_reply_at"}
                  dir={sortState.dir}
                  onSort={() => sortToggle("last_reply_at")}
                  className="hidden lg:table-cell"
                >
                  Dernier post
                </SortableHeader>

                {/* Action — always visible */}
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedFiltered.slice(0, 200).map((f, i) => {
                const isFollowed = followedIds.has(f.id);
                return (
                  <TableRow key={f.id} className="group">
                    {/* # */}
                    <TableCell className="text-foreground-subtle text-xs hidden sm:table-cell">
                      {i + 1}
                    </TableCell>

                    {/* Forum title (sticky) */}
                    <TableCell className="sticky left-0 bg-surface group-hover:bg-surface-muted/50 z-10 min-w-[12rem]">
                      <Link to={`/forums/${f.id}/threads`} className="font-medium hover:text-primary">
                        {f.title || f.slug}
                      </Link>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-foreground-subtle">{f.slug}</span>
                        {/* On mobile, surface the category here since the column itself is hidden. */}
                        <span className="md:hidden">
                          <CategoryBadge category={f.category ?? "unknown"} />
                        </span>
                      </div>
                    </TableCell>

                    {/* Catégorie */}
                    <TableCell className="hidden md:table-cell">
                      <CategoryBadge category={f.category ?? "unknown"} />
                    </TableCell>

                    {/* Cours */}
                    <TableCell className="text-right hidden lg:table-cell">
                      <PriceTag
                        price={f.last_price}
                        variation={f.last_variation_pct}
                        currency={f.price_currency}
                        fetchedAt={f.last_price_at}
                        forumId={f.id}
                        hideVariation
                      />
                    </TableCell>

                    {/* Var. cours */}
                    <TableCell className="text-right hidden md:table-cell">
                      <VariationBadge value={f.last_variation_pct} />
                    </TableCell>

                    {/* Score */}
                    <TableCell className="text-right tabular-nums hidden lg:table-cell">
                      {f.active_score?.toFixed(1) ?? "—"}
                    </TableCell>

                    {/* Sujets (always) */}
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-2 tabular-nums">
                        <span className="text-foreground font-medium">
                          {formatNumber(threadsCur(f))}
                        </span>
                        <TrendBadge current={threadsCur(f)} previous={threadsPrev(f)} />
                      </div>
                    </TableCell>

                    {/* Activité (always) */}
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-2 tabular-nums">
                        <span className="text-foreground font-medium">
                          {formatNumber(postsCur(f))}
                        </span>
                        <TrendBadge current={postsCur(f)} previous={postsPrev(f)} />
                      </div>
                    </TableCell>

                    {/* Dernier post */}
                    <TableCell className="text-foreground-muted text-xs hidden lg:table-cell">
                      {fromNowFR(f.last_reply_at)}
                    </TableCell>

                    {/* Action (always) — icon-only on small screens */}
                    <TableCell className="text-right">
                      <Button
                        variant={isFollowed ? "default" : "outline"}
                        size="sm"
                        onClick={() =>
                          gate(() => toggle.mutate(f), {
                            actionLabel: "suivre un forum",
                          })
                        }
                        disabled={toggle.isPending}
                        aria-label={isFollowed ? "Ne plus suivre" : "Suivre"}
                      >
                        {isFollowed ? <Star className="size-3.5" /> : <StarOff className="size-3.5" />}
                        <span className="hidden sm:inline">
                          {isFollowed ? "Suivi" : "Suivre"}
                        </span>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
        </>
      )}
    </div>
  );
}
